import React, {
    CSSProperties,
    FC,
    KeyboardEvent,
    ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react'

import HistoryIcon from './Icons/HistoryIcon'
import { Segment } from './Segment'
import LayoutDashboardIcon from './Icons/LayoutDashboardIcon'

// Visually mirrors the segmented toggle in the new UI (akixi-frontend's
// LegacyModeToggle). Classic UI is always the active segment when this is
// shown inside CCS — clicking the New UI segment runs the SSO exchange and
// navigates the user across.
//
// All static styling (colours, sizes, animations, palette) lives in
// scss/router/components/ui-version-toggle.scss. The toggle uses the dark
// palette in all themes. Only the sliding-indicator's dynamic `left`/`width`
// come through inline styles because they're computed at runtime from
// getBoundingClientRect.

const ICON_SIZE: number = 13

type IndicatorState = {
    left: number
    width: number
    ready: boolean
}

type Props = {
    classicLabel: string
    newLabel: string
    badgeLabel?: string
    onSwitchToNewUI: () => void
}

export const UiVersionToggle: FC<Props> = ({
    classicLabel,
    newLabel,
    badgeLabel = 'New',
    onSwitchToNewUI
}: Props): ReactElement => {
    // Inside CCS we're always in "classic" mode — there's no in-place toggle, the New UI
    // segment is a navigation away. Classic is permanently the active segment.
    const isNew: boolean = false

    const trayRef = useRef<HTMLDivElement | null>(null)
    const classicRef = useRef<HTMLButtonElement | null>(null)
    const newRef = useRef<HTMLButtonElement | null>(null)
    const [indicator, setIndicator] = useState<IndicatorState>({ left: 0, width: 0, ready: false })
    const [transitionEnabled, setTransitionEnabled] = useState<boolean>(false)

    const measure = useCallback((): void => {
        const tray: HTMLDivElement | null = trayRef.current
        const target: HTMLButtonElement | null = isNew ? newRef.current : classicRef.current
        if (!tray || !target) return
        const trayRect: DOMRect = tray.getBoundingClientRect()
        const targetRect: DOMRect = target.getBoundingClientRect()
        setIndicator({ left: targetRect.left - trayRect.left, width: targetRect.width, ready: true })
    }, [isNew])

    useEffect((): (() => void) | undefined => {
        if (typeof window === 'undefined') return undefined
        let raf2: number | null = null
        const raf1: number = window.requestAnimationFrame((): void => {
            setTransitionEnabled(true)
            raf2 = window.requestAnimationFrame((): void => measure())
        })
        return (): void => {
            window.cancelAnimationFrame(raf1)
            if (raf2 !== null) window.cancelAnimationFrame(raf2)
        }
        // Only run once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useLayoutEffect((): void => {
        if (transitionEnabled) measure()
    }, [measure, transitionEnabled])

    useEffect((): (() => void) | undefined => {
        if (typeof window === 'undefined') return undefined
        window.addEventListener('resize', measure)
        let observer: ResizeObserver | null = null
        if (typeof ResizeObserver !== 'undefined' && trayRef.current) {
            observer = new ResizeObserver(measure)
            observer.observe(trayRef.current)
        }
        return (): void => {
            window.removeEventListener('resize', measure)
            observer?.disconnect()
        }
    }, [measure])

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            onSwitchToNewUI()
        }
    }

    const indicatorStyle: CSSProperties = {
        left: indicator.left,
        width: indicator.width
    }

    const indicatorClassNames: string[] = ['ui-version-toggle__indicator']
    if (isNew) indicatorClassNames.push('ui-version-toggle__indicator--new-selected')
    if (indicator.ready && transitionEnabled) {
        indicatorClassNames.push('ui-version-toggle__indicator--animated')
    }

    return (
        <div
            ref={trayRef}
            role='tablist'
            aria-label='UI version'
            onKeyDown={onKeyDown}
            className='ui-version-toggle'
        >
            <div aria-hidden='true' className={indicatorClassNames.join(' ')} style={indicatorStyle} />

            <Segment ref={classicRef} active={true} aria-selected={true}>
                <HistoryIcon size={ICON_SIZE} />
                <span>{classicLabel}</span>
            </Segment>

            <Segment ref={newRef} active={false} aria-selected={false} onClick={onSwitchToNewUI}>
                <span aria-hidden='true' className='ui-version-toggle__shimmer' />
                <LayoutDashboardIcon size={ICON_SIZE} />
                <span style={{ position: 'relative' }}>{newLabel}</span>
                <span
                    className={
                        'ui-version-toggle__badge' +
                        (isNew ? ' ui-version-toggle__badge--selected' : '')
                    }
                >
                    {badgeLabel}
                </span>
            </Segment>
        </div>
    )
}

